/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Dialog, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm, SubmitHandler } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import AddBoxIcon from '@mui/icons-material/AddBox';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LabelIcon from '@mui/icons-material/Label';
import StraightenIcon from '@mui/icons-material/Straighten';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { NumberFieldFormInput, AutocompleteFormInput, TextFieldFormInput } from '../formInputs';
import StyledButton from '../buttons/StyledButton';
import { LocationProps, LocationType } from '../../datastore/locations';
import { useCurrentUser, useDatastore } from '../../hooks';
import { useEffect } from 'react';
import ToggleFormInput from '../formInputs/toggle';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';

interface NewSSCCSelectFormInputOptionsProps {
  name: string;
  value: string;
  type: string;
}

interface NewSsccFormProps {
  open: boolean;
  onSubmit: (data: NewSsccModel) => void;
  onClose: () => void;
  sites: NewSSCCSelectFormInputOptionsProps[];
  prePopulateSite: boolean;
  originLocation?: LocationProps | undefined;
  isLoading?: boolean;
}

export interface NewSsccModel {
  nickname: string;
  site: string;
  height: string;
  width: string;
  depth: string;
  showDropSites: boolean;
  originLocation?: LocationProps | undefined;
}

export default function NewSsccForm({
  open,
  onSubmit,
  onClose,
  sites,
  isLoading = false,
  prePopulateSite = false,
  originLocation
}: NewSsccFormProps) {
  const defaultValues: NewSsccModel = {
    nickname: '',
    site: '',
    height: '',
    width: '',
    depth: '',
    showDropSites: false
  };
  const store = useDatastore();
  const user = useCurrentUser();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues });

  useEffect(() => {
    if (prePopulateSite) {
      setMatchedLocation();
    }
  }, [originLocation]);

  const onSubmitForm: SubmitHandler<NewSsccModel> = (data) => onSubmit(data);

  async function setMatchedLocation() {
    if (originLocation) {
      let foundSite = null;
      if (originLocation.type === LocationType.mainLocation) {
        foundSite = sites.find((site) => site.name === originLocation.name);
      } else {
        const parentLocation = await store.locations.getById(originLocation.parentLocationId ?? '');
        foundSite = sites.find((site) => site.name === parentLocation?.name);
      }
      setValue('site', foundSite?.value ?? '');
    }
  }
  const showDropSitesValue = watch('showDropSites');
  let sortedSites = sites.sort((a, b) => a.name.localeCompare(b.name));
  if (!showDropSitesValue) {
    sortedSites = sortedSites.filter((site) => site.type === LocationType.mainLocation);
  }

  const theme = useTheme();

  return (
    <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Stack spacing={0}>
          {/* Header */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AddBoxIcon sx={{ fontSize: 24, color: 'text.primary' }} />
                <Typography variant="h6" fontWeight={600}>
                  Create New SSCC
                </Typography>
              </Stack>
              <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ padding: 3, maxHeight: '70vh', overflowY: 'auto', backgroundColor: 'grey.50' }}>
            <Stack spacing={3}>
              {/* Basic Information Card */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  padding: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                <Stack spacing={2.5}>
                  {/* Nickname */}
                  <Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                      <LabelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        SSCC Nickname (Optional)
                      </Typography>
                    </Stack>
                    <TextFieldFormInput name="nickname" label="" control={control} error={errors['nickname']} />
                  </Box>

                  {/* Destination Site */}
                  <Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Destination Site *
                      </Typography>
                    </Stack>
                    <AutocompleteFormInput
                      name="site"
                      control={control}
                      required
                      options={sortedSites}
                      error={errors['site']}
                      label=""
                      placeholder="Search or select..."
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Dimensions Card */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  padding: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <StraightenIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Dimensions
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Please enter the item dimensions in <b>Centimetres</b>
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                      <StraightenIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Length (cm)
                      </Typography>
                    </Stack>
                    <NumberFieldFormInput
                      name="depth"
                      label=""
                      control={control}
                      sx={{ minWidth: 'auto' }}
                      error={errors['depth']}
                      wholeNumbersOnly
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                      <StraightenIcon
                        sx={{
                          fontSize: 14,
                          color: 'text.secondary',
                          transform: 'rotate(90deg)'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Width (cm)
                      </Typography>
                    </Stack>
                    <NumberFieldFormInput
                      name="width"
                      label=""
                      control={control}
                      sx={{ minWidth: 'auto' }}
                      error={errors['width']}
                      wholeNumbersOnly
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                      <StraightenIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Height (cm)
                      </Typography>
                    </Stack>
                    <NumberFieldFormInput
                      name="height"
                      label=""
                      control={control}
                      sx={{ minWidth: 'auto' }}
                      error={errors['height']}
                      wholeNumbersOnly
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Options Card */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  padding: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <VisibilityIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <ToggleFormInput
                    name="showDropSites"
                    control={control}
                    label="Show Drop Sites"
                    error={errors['showDropSites']}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: 'white',
              borderTop: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.08)'
            }}>
            <LoadingSpinner isLoading={isLoading}>
              <StyledButton
                variant="contained"
                type="submit"
                disabled={user.isReadOnly}
                fullWidth
                startIcon={<LocalShippingIcon />}
                sx={{
                  paddingY: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2
                }}>
                Complete + Print
              </StyledButton>
            </LoadingSpinner>
          </Box>
        </Stack>
      </form>
    </Dialog>
  );
}
